/**
 * @flow
 */

import React from 'react';
import gql from 'graphql-tag';
import styled from 'styled-components';
import { format, isAfter, parse } from 'date-fns';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { map, get, filter, isEmpty } from 'lodash';
import { FormattedMessage } from 'react-intl';
import { Modal, Table, EllipsisLoader } from '../../../../components';

// A query to get appointments for specified client identified by ID
const GET_APPOINTMENTS_QUERY = gql`
    query getMembershipBoughtItems(
        $salon: ID!
        $membershipId: ID!
        $lang: String!
    ) {
        viewer {
            id
            ... on Administrator {
                salon(id: $salon) {
                    id
                    memberships {
                        id
                        membershipBoughtItems(id: $membershipId) {
                            id
                            boughtAt
                            paymentType
                            price
                            seller {
                                ... on Master {
                                    id
                                    firstName
                                    lastName
                                }
                                ... on Administrator {
                                    id
                                    firstName
                                    lastName
                                }
                                ... on Cabinet {
                                    id
                                    name(lang: $lang)
                                }
                            }
                            entity {
                                ... on Service {
                                    id
                                    name(lang: $lang)
                                }
                                ... on Product {
                                    id
                                    name
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`;

type AppointmentsProps = {
    boughtItems: Array<any>,
    loading: boolean,
};

type AppointmentRowProps = {
    boughtItem: Object,
};

/**
 * Show this message if list of appointments is empty
 */
const EmptyMessage = () => (
    <Table.Row className="transparent">
        <Table.Cell colSpan={4} textAlign="center">
            <FormattedMessage
                id="pages.memberships.details.appointments.noAppointments"
                defaultMessage="This membership has not been used yet"
            />
        </Table.Cell>
    </Table.Row>
);

const LoadMoreSpinner = () => <EllipsisLoader size="1em" />;

/**
 * A row of single appointment
 */
const AppointmentRow = ({ boughtItem }: AppointmentRowProps) => {
    const styledRow = isAfter(
        parse(boughtItem.boughtAt, 'dd.MM.yyyy HH:mm', new Date()),
        new Date(),
    );
    const TableRow = styled(Table.Row)`
        ${styledRow && ' font-weight: bold;'}
    `;

    return (
        <TableRow>
            <Table.Cell>
                {/* we need to parse number from string, because we receive timestamp from the server as a string
            this is a temporary solution until the problem with the data types in graphql is resolved */}
                {boughtItem.boughtAt}
            </Table.Cell>
            <Table.Cell>
                {`${boughtItem.firstName} ${boughtItem.lastName || ''}`}
            </Table.Cell>
            <Table.Cell>{boughtItem.name}</Table.Cell>
            <Table.Cell textAlign="right">
                {`${boughtItem.price.toFixed(2)} €`}
            </Table.Cell>
        </TableRow>
    );
};

const MembershipAppointmentsView = ({
    boughtItems,
    loading,
}: AppointmentsProps) => {
    return (
        <Modal.Context>
            <Modal.Content>
                {loading ? (
                    <LoadMoreSpinner key={0} />
                ) : (
                    <Table celled striped selectable>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>
                                    <FormattedMessage
                                        id="pages.clients.details.appointments.date"
                                        defaultMessage="Date"
                                    />
                                </Table.HeaderCell>
                                <Table.HeaderCell>
                                    <FormattedMessage
                                        id="pages.clients.details.appointments.master"
                                        defaultMessage="Master"
                                    />
                                </Table.HeaderCell>
                                <Table.HeaderCell>
                                    <FormattedMessage
                                        id="pages.clients.details.appointments.service"
                                        defaultMessage="Service"
                                    />
                                </Table.HeaderCell>
                                <Table.HeaderCell textAlign="right">
                                    <FormattedMessage
                                        id="pages.clients.details.appointments.price"
                                        defaultMessage="Price"
                                    />
                                </Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {isEmpty(boughtItems) ? (
                                <EmptyMessage />
                            ) : (
                                map(boughtItems, (item, key) => (
                                    <AppointmentRow
                                        key={key}
                                        boughtItem={item}
                                    />
                                ))
                            )}
                        </Table.Body>
                    </Table>
                )}
            </Modal.Content>
        </Modal.Context>
    );
};

function MembershipAppointments({ membership }) {
    const salon = useSelector(({ user }) => user.get('salon'));
    const currentLanguage = useSelector(
        ({ intl }) => intl.get('locale') || intl.get('defaultLanguage'),
    );
    const { loading, data } = useQuery(GET_APPOINTMENTS_QUERY, {
        variables: {
            salon,
            membershipId: membership.id,
            lang: currentLanguage,
        },
        fetchPolicy: 'cache-and-network',
    });
    const boughtItems = filter(
        map(
            get(data, 'viewer.salon.memberships[0].membershipBoughtItems'),
            item => ({
                id: item.id,
                boughtAt: format(new Date(item.boughtAt), 'dd.MM.yyyy HH:mm'),
                price: item.price,
                name: item.entity.name,
                firstName: item.seller.firstName || item.seller.name,
                lastName: item.seller.lastName || '',
                paymentType: item.paymentType,
            }),
        ),
        i => i.paymentType === 'MEMBERSHIP',
    );
    return <MembershipAppointmentsView boughtItems={boughtItems} loading={loading} />;
}

export default MembershipAppointments;
